import { notifySseActivity } from 'components/DevRenderOverlay/sseActivityHelper';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  addDeviceFromEvent,
  applyDeviceUpdatesFromEvent,
  getConnectedDevicesEventSource_v2,
  removeDeviceFromEvent,
} from 'services/devices/devices.service';
import {
  addGroupFromEvent,
  removeGroupFromEvent,
  updateGroupFromEvent,
} from 'services/devices/groups/groups.service';
import {
  addSystemFromEvent,
  removeSystemFromEvent,
  updateSystemFromEvent,
} from 'services/integrations/integrations.service';

// Hook for Integration EventSource wiring.
export default function useIntegrationEvents({ enabled = true } = {}) {
  const dispatch = useDispatch();
  const eventSourceRef = useRef(null);

  // Set up EventSource lifecycle and clean up on unmount.
  useEffect(() => {
    if (!enabled || eventSourceRef.current) return;

    const eventSource = getConnectedDevicesEventSource_v2();
    if (!eventSource) return;
    eventSourceRef.current = eventSource;

    const safeParse = (evt) => {
      // Guard against malformed SSE payloads.
      try {
        return JSON.parse(evt?.data || '{}');
      } catch (error) {
        console.error('EventSource parse error:', error);
        return null;
      }
    };

    const deviceUpdateListener = (evt) => {
      // Merge incoming device updates with cached device data.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('device-update');
      applyDeviceUpdatesFromEvent(dispatch, payload);
    };

    const deviceAddListener = (evt) => {
      // Insert newly added devices into the list.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('device-add');
      addDeviceFromEvent(dispatch, payload);
    };

    const deviceRemoveListener = (evt) => {
      // Remove deleted devices from the list.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('device-remove');
      removeDeviceFromEvent(dispatch, payload);
    };

    const groupUpdateListener = (evt) => {
      // Update an existing device group payload.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('group-update');
      updateGroupFromEvent(dispatch, payload);
    };

    const groupAddListener = (evt) => {
      // Append newly added device groups.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('group-add');
      addGroupFromEvent(dispatch, payload);
    };

    const groupRemoveListener = (evt) => {
      // Remove deleted device groups.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('group-remove');
      removeGroupFromEvent(dispatch, payload.id);
    };

    const systemUpdateListener = (evt) => {
      // Update an existing integration/system entry.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('system-update');
      updateSystemFromEvent(dispatch, payload);
    };

    const systemAddListener = (evt) => {
      // Add a newly connected integration/system.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('system-add');
      addSystemFromEvent(dispatch, payload);
    };

    const systemRemoveListener = (evt) => {
      // Remove a disconnected integration/system.
      const payload = safeParse(evt);
      if (!payload) return;
      notifySseActivity('system-remove');
      removeSystemFromEvent(dispatch, payload);
    };

    eventSource.addEventListener('device-update', deviceUpdateListener);
    eventSource.addEventListener('device-update-user', deviceUpdateListener);
    eventSource.addEventListener('device-add', deviceAddListener);
    eventSource.addEventListener('device-remove', deviceRemoveListener);

    eventSource.addEventListener('group-update', groupUpdateListener);
    eventSource.addEventListener('group-add', groupAddListener);
    eventSource.addEventListener('group-remove', groupRemoveListener);

    eventSource.addEventListener('system-update', systemUpdateListener);
    eventSource.addEventListener('system-add', systemAddListener);
    eventSource.addEventListener('system-remove', systemRemoveListener);

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, [dispatch, enabled]);
}
